import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar as CalendarWidget } from '../components/ui/calendar';
import { Search, Loader2, Receipt, IndianRupee, Download, Calendar as CalendarIcon } from 'lucide-react';
import { printInvoice } from '../lib/invoicePrint';
import { paymentBadge } from '../lib/paymentStatus';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DoctorVendorBillsPage() {
  const [bills, setBills] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [vendorName, setVendorName] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sort, setSort] = useState('created_at:-1');
  const [paymentFilter, setPaymentFilter] = useState('');
  const [selectedBill, setSelectedBill] = useState(null);

  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('clinic_token')}` });

  const applyDatePreset = (preset) => {
    const fmt = d => d.toISOString().slice(0, 10);
    const today = new Date();
    let start = new Date(today), end = new Date(today);
    if (preset === 'yesterday') { start.setDate(start.getDate() - 1); end.setDate(end.getDate() - 1); }
    else if (preset === 'this_month') { start = new Date(today.getFullYear(), today.getMonth(), 1); }
    else if (preset === 'last_30') { start.setDate(start.getDate() - 30); }
    setDateFrom(fmt(start));
    setDateTo(fmt(end));
  };

  const fetchBills = useCallback(async () => {
    setLoading(true);
    try {
      const [sort_by, sort_order] = sort.split(':');
      const params = new URLSearchParams({ sort_by, sort_order });
      if (search) params.append('search', search);
      if (vendorName) params.append('vendor_name', vendorName);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (paymentFilter) params.append('payment_status', paymentFilter);
      const res = await axios.get(`${API}/api/chemist/walkin-bills/doctor/mine?${params}`, { headers: getHeaders() });
      setBills(res.data.bills || []);
      setTotal(res.data.total || 0);
    } catch { toast.error('Failed to load vendor bills'); }
    setLoading(false);
  }, [search, vendorName, dateFrom, dateTo, sort, paymentFilter]);

  useEffect(() => { fetchBills(); }, [fetchBills]);

  return (
    <Layout>
      <div className="p-4 md:p-6 space-y-4" data-testid="doctor-vendor-bills-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Receipt className="w-6 h-6 text-teal-600" /> Vendor Bills</h1>
          <p className="text-sm text-slate-500">Bills raised by your connected chemist/pharma vendors that reference you as the referring doctor</p>
        </div>

        <Card>
          <CardHeader className="pb-2">
            <div className="flex flex-wrap gap-2">
              <Input placeholder="Search patient/invoice..." value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && fetchBills()} className="w-48 h-9" data-testid="vendor-bills-search" />
              <Input placeholder="Vendor name..." value={vendorName} onChange={(e) => setVendorName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && fetchBills()} className="w-40 h-9" data-testid="vendor-bills-vendor-filter" />
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="h-9 text-sm justify-start" data-testid="vendor-bills-date-popover-trigger">
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {dateFrom ? `${dateFrom} to ${dateTo}` : 'Pick date range'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-3" align="start">
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => applyDatePreset('today')} data-testid="vb-date-preset-today">Today</Button>
                    <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => applyDatePreset('yesterday')} data-testid="vb-date-preset-yesterday">Yesterday</Button>
                    <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => applyDatePreset('this_month')} data-testid="vb-date-preset-this-month">This Month</Button>
                    <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => applyDatePreset('last_30')} data-testid="vb-date-preset-last-30">Last 30 Days</Button>
                  </div>
                  <CalendarWidget
                    mode="range"
                    selected={{ from: dateFrom ? new Date(dateFrom) : undefined, to: dateTo ? new Date(dateTo) : undefined }}
                    onSelect={(range) => {
                      const fmt = d => d.toISOString().slice(0, 10);
                      setDateFrom(range?.from ? fmt(range.from) : '');
                      setDateTo(range?.to ? fmt(range.to) : (range?.from ? fmt(range.from) : ''));
                    }}
                    numberOfMonths={1}
                  />
                </PopoverContent>
              </Popover>
              <select value={sort} onChange={(e) => setSort(e.target.value)} className="h-9 text-sm border rounded px-2" data-testid="vendor-bills-sort">
                <option value="created_at:-1">Newest First</option>
                <option value="created_at:1">Oldest First</option>
                <option value="total:-1">Total: High to Low</option>
                <option value="total:1">Total: Low to High</option>
              </select>
              <select value={paymentFilter} onChange={(e) => setPaymentFilter(e.target.value)} className="h-9 text-sm border rounded px-2" data-testid="vendor-bills-payment-filter">
                <option value="">All Payment Status</option>
                <option value="paid">Paid</option>
                <option value="partial">Partial</option>
                <option value="unpaid">Unpaid</option>
              </select>
              <Button size="sm" variant="outline" onClick={fetchBills} data-testid="vendor-bills-apply-filter"><Search className="w-4 h-4 mr-1" /> Apply</Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-teal-600" /></div>
            ) : bills.length === 0 ? (
              <p className="text-center text-slate-400 py-10">No vendor bills found</p>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-slate-500 mb-2">{total} bill(s) found</p>
                {bills.map(bill => (
                  <div key={bill.id} className="p-3 border rounded-lg hover:bg-slate-50 cursor-pointer flex items-center justify-between" onClick={() => setSelectedBill(bill)} data-testid={`vendor-bill-${bill.id}`}>
                    <div>
                      <div className="font-medium text-sm">{bill.invoice_number} <Badge variant="secondary" className="text-xs ml-1">{bill.vendor_name}</Badge></div>
                      <p className="text-xs text-slate-500">{bill.patient_name} {bill.patient_phone && `• ${bill.patient_phone}`}</p>
                      <p className="text-xs text-slate-400">{new Date(bill.created_at).toLocaleString()}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-emerald-600 flex items-center gap-1 justify-end"><IndianRupee className="w-3 h-3" />{bill.total?.toFixed(2)}</p>
                      <Badge className={`text-xs ${paymentBadge(bill.payment_status).className}`}>{paymentBadge(bill.payment_status).label}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={!!selectedBill} onOpenChange={(o) => !o && setSelectedBill(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader className="flex-row items-center justify-between pr-8">
              <DialogTitle>Invoice: {selectedBill?.invoice_number}</DialogTitle>
              {selectedBill && (
                <Button size="sm" variant="outline" onClick={() => printInvoice(selectedBill)} data-testid="download-invoice-btn">
                  <Download className="w-4 h-4 mr-1" /> Download
                </Button>
              )}
            </DialogHeader>
            {selectedBill && (
              <div className="space-y-3 text-sm">
                <div className="border-b pb-2 flex items-start gap-3">
                  {selectedBill.vendor_logo && (
                    <img src={`${API}${selectedBill.vendor_logo}`} alt="Logo" className="w-12 h-12 object-contain rounded border" />
                  )}
                  <div>
                    <p className="font-bold">{selectedBill.vendor_name}</p>
                    <p className="text-xs text-slate-500">{selectedBill.vendor_address}</p>
                    <p className="text-xs text-slate-500">GST: {selectedBill.vendor_gst} | Ph: {selectedBill.vendor_phone}</p>
                  </div>
                </div>
                <div><strong>Patient:</strong> {selectedBill.patient_name} {selectedBill.patient_phone && `| ${selectedBill.patient_phone}`}</div>
                <div><strong>Date:</strong> {new Date(selectedBill.created_at).toLocaleString()}</div>
                {selectedBill.billed_to && (selectedBill.billed_to.gst_number || selectedBill.billed_to.address || selectedBill.billed_to.clinic_name) && (
                  <div className="bg-slate-50 p-2 rounded text-xs space-y-0.5">
                    <p className="font-medium">{selectedBill.billed_to.clinic_name || selectedBill.billed_to.name}</p>
                    {selectedBill.billed_to.gst_number && <p>GSTIN: {selectedBill.billed_to.gst_number}</p>}
                    {selectedBill.billed_to.address && <p>{selectedBill.billed_to.address}</p>}
                  </div>
                )}
                <table className="w-full text-xs border"><thead><tr className="bg-slate-100"><th className="p-1 text-left">Item</th><th className="p-1">Qty</th><th className="p-1">Price</th><th className="p-1">Total</th></tr></thead><tbody>
                  {selectedBill.items?.map((item, i) => (
                    <tr key={`row-${i}`} className="border-t"><td className="p-1">{item.name}</td><td className="p-1 text-center">{item.quantity}</td><td className="p-1 text-right">{item.selling_price}</td><td className="p-1 text-right">{(item.selling_price * item.quantity).toFixed(2)}</td></tr>
                  ))}
                </tbody></table>
                <div className="border-t pt-2 space-y-1">
                  <div className="flex justify-between"><span>Subtotal</span><span>{selectedBill.subtotal?.toFixed(2)}</span></div>
                  {selectedBill.discount > 0 && <div className="flex justify-between text-red-500"><span>Discount</span><span>-{selectedBill.discount?.toFixed(2)}</span></div>}
                  <div className="flex justify-between"><span>GST ({selectedBill.gst_percentage}%)</span><span>{selectedBill.gst_amount?.toFixed(2)}</span></div>
                  <div className="flex justify-between font-bold text-lg"><span>Total</span><span>{selectedBill.total?.toFixed(2)}</span></div>
                </div>
                <div className="flex items-center justify-between text-xs bg-slate-50 p-2 rounded">
                  <span className="font-medium">Payment Status</span>
                  <div className="flex items-center gap-2">
                    <Badge className={paymentBadge(selectedBill.payment_status).className}>{paymentBadge(selectedBill.payment_status).label}</Badge>
                    <span className="text-slate-500">Due: {(selectedBill.balance_due ?? (selectedBill.total - (selectedBill.amount_paid || 0))).toFixed(2)}</span>
                  </div>
                </div>
                {selectedBill.payment_mode && (
                  <div className="text-xs text-slate-500 flex justify-between">
                    <span>Paid via {selectedBill.payment_mode.replace('_', ' ')}</span>
                    {selectedBill.transaction_id && <span>Txn: {selectedBill.transaction_id}</span>}
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
